import React from 'react'
import Table from '../../components/common/Table'
import { CompetitionEntryDTO } from '../../services/apiClient'
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime'
import { getBadgeColors, renderBooleanBadge } from '../../utils/displayUtils'
import FileIcons from '../common/FileIcons'
import Badge from '../common/Badge'
import createCompetitionEntryActions from '../actionMenus/CompetitionEntryActions'

interface CompetitionEntryTableProps {
    data: CompetitionEntryDTO[];
    handleConfirm: (row: CompetitionEntryDTO) => void;
    handleReject: (row: CompetitionEntryDTO) => void;
    currentPage: number;
    totalPages: number;
    setCurrentPage: (page: number) => void;
    pageSize: number;
    setPageSize: (size: number) => void;
    isLoading: boolean;
    selectedIds?: Set<string>;
    onSelectRow?: (id: string, isSelected: boolean) => void;
    onSelectAll?: (isSelected: boolean) => void;
    sortBy?: string;
    sortDescending?: boolean;
    onSort?: (sortKey: string) => void;
}

const CompetitionEntryTable: React.FC<CompetitionEntryTableProps> = ({
    data,
    handleConfirm,
    handleReject,
    currentPage,
    totalPages,
    setCurrentPage,
    pageSize,
    setPageSize,
    isLoading,
    selectedIds = new Set(),
    onSelectRow,
    onSelectAll,
    sortBy,
    sortDescending,
    onSort,
}) => {
    const allCurrentPageIds = data.map(row => row.id).filter((id): id is string => !!id);
    const allSelected = allCurrentPageIds.length > 0 && allCurrentPageIds.every(id => selectedIds.has(id));
    const someSelected = allCurrentPageIds.some(id => selectedIds.has(id)) && !allSelected;

    return (
        <Table
            columns={[
                ...(onSelectRow && onSelectAll
                    ? [
                        {
                            label: (
                                <input
                                    type="checkbox"
                                    checked={allSelected}
                                    ref={(el) => {
                                        if (el) el.indeterminate = someSelected;
                                    }}
                                    onChange={(e) => onSelectAll?.(e.target.checked)}
                                    className="w-4 h-4 rounded border-gray-300 dark:border-gray-600"
                                />
                            ),
                            key: 'checkbox',
                            render: (row: CompetitionEntryDTO) => (
                                <input
                                    type="checkbox"
                                    checked={selectedIds.has(row.id ?? '')}
                                    onChange={(e) => onSelectRow?.(row.id ?? '', e.target.checked)}
                                    onClick={(e) => e.stopPropagation()}
                                    className="w-4 h-4 rounded border-gray-300 dark:border-gray-600"
                                />
                            ),
                        },
                    ]
                    : []),
                {
                    label: 'Member',
                    key: 'member',
                    sortable: true,
                    sortKey: 'FirstName',
                    render: (row: CompetitionEntryDTO) => (
                        <div
                            className="flex flex-col gap-1"
                        >
                            <span>
                                {row.member?.firstName} {row.member?.lastName}
                            </span>
                            <span>{row.member?.idPassportNumber ?? ''}</span>
                        </div>
                    ),
                },

                {
                    label: "Class",
                    key: "competitionClassId",
                    sortable: true,
                    sortKey: 'CompetitionClassTitle',
                    render: (row: CompetitionEntryDTO) => (
                        <span>{row.competitionClass?.title}</span>
                    )
                },

                {
                    label: "Year",
                    key: "year",
                    sortable: true,
                    sortKey: 'Year',
                },

                {
                    label: "Age",
                    key: "age",
                    sortable: true,
                    sortKey: 'Age',
                },

                {
                    label: "Amount Expected",
                    key: "amount",
                    sortable: true,
                    sortKey: 'CompetitionAmount',
                    render: (row: CompetitionEntryDTO) => (
                        <span>{row.competition?.amount?.toFixed(2)}</span>
                    )
                },

                {
                    label: 'Proof',
                    key: 'proofOfPaymentFileId',
                    sortable: true,
                    sortKey: 'ProofOfPaymentFileId',
                    render: (row: CompetitionEntryDTO) => (
                        <FileIcons
                            files={[
                                {
                                    id: row.proofOfPaymentFileId,
                                    label: 'Proof of payment',
                                },
                            ]}
                        />
                    ),
                },

                {
                    label: "Courier Street",
                    key: "courierStreet",
                    sortable: true,
                    sortKey: 'CourierStreet',
                },

                {
                    label: "Courier Suburb",
                    key: "courierSuburb",
                    sortable: true,
                    sortKey: 'CourierSuburb',
                },

                {
                    label: "Courier Province",
                    key: "courierProvinceId",
                    sortable: true,
                    sortKey: 'CourierProvinceTitle',
                    render: (row: CompetitionEntryDTO) => (
                        <span>{row.courierProvince?.title}</span>
                    )
                },

                {
                    label: "Courier Code",
                    key: "courierCode",
                    sortable: true,
                    sortKey: 'CourierCode',
                },

                {
                    label: "Courier Note",
                    key: "courierNote",
                    sortable: true,
                    sortKey: 'CourierNote',
                    render: (row: CompetitionEntryDTO) => (
                        <span>{row.courierNote || '-'}</span>
                    )
                },

                {
                    label: 'Status',
                    key: 'competitionStatusId',
                    sortable: true,
                    sortKey: 'CompetitionStatusTitle',
                    render: (row: CompetitionEntryDTO) => {
                        if (row.isDeleted) {
                            return <Badge label="Removed" variant="error" />;
                        }
                        const base = row.competitionStatus?.colourGrading?.colour ?? '#ffffffff';
                        const { bg, text } = getBadgeColors(base, 0.18, 0.4);
                        return base ? (
                            <Badge
                                label={row.competitionStatus?.title || 'Unknown'}
                                variant="custom"
                                customBgColor={bg}
                                customTextColor={text}
                            />
                        ) : (
                            <span>{row.competitionStatus?.title || 'Unknown'}</span>
                        );
                    },
                },

                {
                    label: "Paid",
                    key: "isPaymentConfirmed",
                    sortable: true,
                    sortKey: 'IsPaymentConfirmed',
                    render: (row: CompetitionEntryDTO) => {
                        if (row.isDeleted) {
                            return <Badge label="Removed" variant="error" />;
                        }
                        if (row.proofOfPaymentFileId && !row.isPaymentConfirmed) {
                            return <Badge label="Pop Submitted" variant="warning" />;
                        }
                        return renderBooleanBadge(row.isPaymentConfirmed ?? false, 'Paid', 'Pending')
                    }
                },

                {
                    label: "Added",
                    key: "createdUtc",
                    sortable: true,
                    sortKey: 'CreatedUtc',
                    format: (value: Date) => convertToUserTime(value?.toString() ?? ''),
                },

            ]}
            data={data}
            actions={{
                extra: (row) =>
                    createCompetitionEntryActions({
                        onConfirm: handleConfirm,
                        onReject: handleReject,
                    })(row),
            }}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            isLoading={isLoading}
            sortBy={sortBy}
            sortDescending={sortDescending}
            onSort={onSort}
        />
    );
};

export default CompetitionEntryTable;
